import { useEffect, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useT } from '../../i18n/context'

export function Dialog({
    open,
    onClose,
    title,
    children,
    wide = false,
}: {
    open: boolean
    onClose: () => void
    title: string
    children: ReactNode
    wide?: boolean
}) {
    const t = useT()

    useEffect(() => {
        if (!open) return
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose()
        }
        document.addEventListener('keydown', onKey)
        return () => document.removeEventListener('keydown', onKey)
    }, [open, onClose])

    if (!open) return null

    return createPortal(
        <div
            className='fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 dark:bg-black/70'
            onMouseDown={e => {
                if (e.target === e.currentTarget) onClose()
            }}
        >
            <div
                role='dialog'
                aria-modal='true'
                aria-label={title}
                className={`max-h-[90vh] w-full overflow-y-auto rounded-xl bg-white dark:bg-slate-900 shadow-xl ${wide ? 'max-w-2xl' : 'max-w-md'}`}
            >
                <div className='flex items-center justify-between border-b border-slate-200 dark:border-slate-700 px-5 py-3.5'>
                    <h2 className='text-sm font-semibold text-slate-700 dark:text-slate-200'>
                        {title}
                    </h2>
                    <button
                        onClick={onClose}
                        aria-label={t('common.close')}
                        className='rounded-md p-1 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-600'
                    >
                        <svg
                            className='h-4 w-4'
                            fill='none'
                            viewBox='0 0 24 24'
                            stroke='currentColor'
                            strokeWidth={2}
                        >
                            <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                d='M6 18L18 6M6 6l12 12'
                            />
                        </svg>
                    </button>
                </div>
                <div className='px-5 py-4'>{children}</div>
            </div>
        </div>,
        document.body,
    )
}
